import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Add from "@mui/icons-material/Add";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Delete from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import DragIndicator from "@mui/icons-material/DragIndicator";
import {
  Box,
  Divider,
  IconButton,
  IconButtonProps,
  InputLabel,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  MenuItemProps,
  MenuList,
  MenuProps,
  PopoverProps,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import * as R from "ramda";
import React, { useEffect, useRef, useState } from "react";
import { useMenu, UseMenuProps } from "../../../hook/menu";
import {
  useAddPropertyType,
  useDeleteProperty,
  useFolderPrimaryKey,
  useFolderPropertyTypes,
  useFolderSecIndexes,
  useGetProperty,
  useRenameProperty,
  useSetFolderPropertyType,
  useSetFolderPropertyTypeIsPKey,
  useSetFolderPropertyTypeIsSecIndex,
  useWriteFolderPropertyTypesAndPKey,
} from "../../../hook/page/property";
import { NotebookMoreMenuSelection } from "../../../types/notebook/database";
import {
  PropertyType,
  PropertyTypeDisplayName,
  PropertyTypeKey,
  initialPropertyTypeValue,
  isReservedPropertyName,
} from "../../../types/page/property";
import {
  PropertyTypeSelect,
  PropertyTypeSelectOption,
} from "../../../types/page/propertyType/select";
import { PropertyTypeText } from "../../../types/page/propertyType/text";
import { useSelectedNotebookPageIdMap } from "../../../hook/page";
import {
  SelectOptionMenu,
  PropertyViewTypeOptionsSelect,
  PropertyViewTypeOptionsSelectOption,
} from "../../page/propertyTypeOptions";

export const PropertyViewTypeOptionsText: React.FC<{
  prop?: PropertyType;
  setProp: (prop: PropertyType) => void;
}> = ({ prop }) => {
  return <></>;
};

export const PropertyViewTypeOptions: React.FC<{
  prop?: PropertyType;
  setProp: (prop: PropertyType) => void;
}> = ({ prop, setProp }) => {
  const propType = prop?.["prop-type"];

  if (!propType) {
    return <></>;
  }

  switch (propType) {
    case PropertyTypeKey.TEXT:
      return <PropertyViewTypeOptionsText prop={prop} setProp={setProp} />;
    case PropertyTypeKey.SELECT: {
      const propOptions = (prop as PropertyTypeSelect)?.options ?? [];
      const setOptionsFn = (opts: Array<{ label: string }>) =>
        setProp({ ...(prop as PropertyTypeSelect), options: opts } as PropertyTypeSelect);
      return <PropertyViewTypeOptionsSelect options={propOptions} setOptions={setOptionsFn} />;
    }
  }
};

// export const AddPropertyItemKey: React.FC<{
//   typeKey: PropertyTypeKey;
//   setMenuSelection: (selection: NotebookMoreMenuSelection) => void;
// }> = ({ setMenuSelection, typeKey }) => {
//   const addProperty = useAddProperty();
//   const [propId, setPropId] = useSelectedPropertyId();
//   const display = PropertyTypeDisplayName[typeKey];
//   const { selectedNotebookPage } = useSelectedNotebookPageIdMap();
//   const folderId = selectedNotebookPage?.["folder-id"];

//   return (
//     <>
//       <MenuItem
//         sx={{ width: "15rem" }}
//         onClick={() => {
//           const data = initialPropertyTypeValue(typeKey, folderId!);
//           setPropId(undefined);
//           addProperty({ data });
//           setMenuSelection(NotebookMoreMenuSelection.PROPERTY);
//         }}
//       >
//         <ListItemText>{display}</ListItemText>
//         <ListItemSecondaryAction>
//           <IconButton color="primary">
//             <Add />
//           </IconButton>
//         </ListItemSecondaryAction>
//       </MenuItem>
//     </>
//   );
// };

// export const AddPropertyMenuItems: React.FC<{
//   setMenuSelection: (selection: NotebookMoreMenuSelection) => void;
// }> = ({ setMenuSelection }) => {
//   const keys = Object.values(PropertyTypeKey);
//   return (
//     <>
//       <MenuItem
//         sx={{ width: "15rem" }}
//         onClick={() => {
//           setMenuSelection(NotebookMoreMenuSelection.PROPERTIES);
//         }}
//       >
//         <ListItemIcon>
//           <ArrowBack color="primary" fontSize="small" />
//         </ListItemIcon>
//         <ListItemText>Go Back</ListItemText>
//       </MenuItem>
//       <MenuItem sx={{ width: "15rem" }} disabled={true}>
//         <ListItemText>Property Type</ListItemText>
//       </MenuItem>
//       {keys.map((key) => {
//         return (
//           <AddPropertyItemKey
//             typeKey={key}
//             key={key}
//             setMenuSelection={setMenuSelection}
//           />
//         );
//       })}
//     </>
//   );
// };

export const OpenAddPropertyTypeMenuItem: React.FC<{
  propType: PropertyTypeKey;
}> = ({ propType }) => {
  const addProperty = useAddPropertyType();
  const display = PropertyTypeDisplayName[propType];
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();

  return (
    <>
      <MenuItem
        data-testid={`add-column-type-${propType}`}
        onClick={(e) => {
          const initData = initialPropertyTypeValue(
            propType,
            selectedNotebookPageId!,
          );
          addProperty({ data: initData });
        }}
      >
        <ListItemText>{display}</ListItemText>
      </MenuItem>
    </>
  );
};

export const AddPropertyMenu: React.FC<MenuProps> = (menuProps) => {
  const keys = Object.values(PropertyTypeKey);
  return (
    <Menu {...menuProps}>
      <MenuItem sx={{ width: "15rem" }} disabled={true}>
        <ListItemText>Property Type</ListItemText>
      </MenuItem>
      {keys.map((key) => {
        return <OpenAddPropertyTypeMenuItem key={key} propType={key} />;
      })}
    </Menu>
  );
};

export const EditPropertyMenuItems: React.FC<{
  propName: string | undefined;
  onClose: PopoverProps["onClose"];
}> = ({ propName, onClose }) => {
  const { selectedNotebookPage, selectedNotebookPageId } =
    useSelectedNotebookPageIdMap();
  const { folderProps } = useFolderPropertyTypes(selectedNotebookPageId);
  const prop = propName ? (folderProps ?? {})[propName] : undefined;
  const propTypeKeys = Object.values(PropertyTypeKey);
  const propData = prop as PropertyTypeText | undefined;
  const deleteProperty = useDeleteProperty(selectedNotebookPageId);
  const [edits, setEdits] = useState<PropertyType | undefined>(undefined);
  const editType = edits?.["prop-type"];
  const editName = edits?.name;
  const setName = (name: string) => setEdits({ ...propData!, name });
  const { setFolderProp } = useSetFolderPropertyType(
    selectedNotebookPageId,
    propName,
  );
  const { folderPKey } = useFolderPrimaryKey(selectedNotebookPageId);
  const isPKey = Boolean(
    propName && folderPKey && folderPKey.includes(propName),
  );
  const setType = (type: PropertyTypeKey) => {
    const initVal = initialPropertyTypeValue(type, selectedNotebookPageId!);
    const newVal = { ...initVal, name: propName! };
    setEdits(newVal);
  };
  const [pKeyEdit, setPKeyEdit] = useState(false);
  const { setFolderPropIsPKey: setIsPKey } = useSetFolderPropertyTypeIsPKey(
    selectedNotebookPageId,
    propName,
  );
  const { folderSecIndexes } = useFolderSecIndexes(selectedNotebookPageId);
  const isSecIndex = Boolean(
    propName && folderSecIndexes && folderSecIndexes.includes(propName),
  );
  const [secIndexEdit, setSecIndexEdit] = useState(false);
  const { setFolderPropIsSecIndex: setIsSecIndex } =
    useSetFolderPropertyTypeIsSecIndex(selectedNotebookPageId, propName);
  const writeBackend = useWriteFolderPropertyTypesAndPKey(
    selectedNotebookPageId,
  );
  const doRenameProperty = useRenameProperty(selectedNotebookPageId);

  const handleSave = async () => {
    if (isReservedPropertyName(editName)) return;
    const renamed = Boolean(edits?.name && propName && edits.name !== propName);
    if (renamed) {
      // Route through the dedicated rename-property op (C11). This migrates
      // prop_vals keys, folder_pkey, folder_sec_indexes, sec_index old→new
      // server-side and re-fetches all three caches. The post-rename writes
      // below are skipped because they use the closure-captured OLD propName
      // (useSetFolderPropertyType / useSetFolderPropertyTypeIsPKey /
      // useSetFolderPropertyTypeIsSecIndex are all constructed with propName)
      // and would re-introduce the old name via the destructive dissoc+assoc
      // / set-folder-properties path. The rename op already migrated name,
      // pkey, and sec-index correctly server-side.
      await doRenameProperty(propName!, edits!.name);
      onClose?.({}, "backdropClick");
      return;
    }
    const newProps = setFolderProp(edits!);
    const newPKey = setIsPKey(pKeyEdit);
    const newSecIndexes = setIsSecIndex(secIndexEdit);
    writeBackend({
      folderProps: newProps!,
      pKey: newPKey!,
      secIndexes: newSecIndexes,
    });
    onClose?.({}, "backdropClick");
  };

  const handleDelete = () => {
    onClose?.({}, "backdropClick");
    deleteProperty(prop?.name!);
  };

  useEffect(() => {
    setPKeyEdit(isPKey);
    setSecIndexEdit(isSecIndex);
    setEdits(prop);
  }, [prop, isPKey, isSecIndex]);

  return (
    <>
      <MenuItem sx={{ width: "15rem" }} disabled={true}>
        <ListItemText>Property</ListItemText>
      </MenuItem>
      <Box sx={{ width: "15rem", pointerEvents: "auto", padding: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Typography>In Primary Key</Typography>
          <Switch
            checked={pKeyEdit}
            onChange={(e) => {
              setPKeyEdit((v) => !v);
            }}
            size="small"
            data-testid={`set-primary-key-${propName}`}
            inputProps={{ "data-testid": `set-primary-key-${propName}` } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <Typography>Secondary Index</Typography>
          <Switch
            checked={secIndexEdit}
            onChange={(e) => {
              setSecIndexEdit((v) => !v);
            }}
            size="small"
            data-testid={`toggle-sec-index-${propName}`}
            inputProps={{ "data-testid": `toggle-sec-index-${propName}` } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        </Box>
      </Box>
      <Box
        component="form"
        sx={{
          padding: "1rem",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          alignItems: "start",
          width: "15rem",
        }}
      >
        <TextField
          value={editName}
          label="name"
          error={isReservedPropertyName(editName)}
          helperText={
            isReservedPropertyName(editName)
              ? '"Page" and "Name" are reserved columns'
              : undefined
          }
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
        <InputLabel id="prop-view-menu-type">Property Type</InputLabel>
        {editType && (
          <Select
            labelId="prop-view-menu-type"
            sx={{ width: "100%" }}
            value={editType}
            label="Type"
            onChange={(e) => setType(e.target.value as PropertyTypeKey)}
          >
            {propTypeKeys.map((key) => {
              const display = PropertyTypeDisplayName[key];
              return (
                <MenuItem key={key} value={key}>
                  {display}
                </MenuItem>
              );
            })}
          </Select>
        )}
      </Box>
      <PropertyViewTypeOptions prop={edits} setProp={setEdits} />
      <MenuItem
        data-testid="prop-edit-save"
        disabled={isReservedPropertyName(editName)}
        onClick={(e) => {
          handleSave();
        }}
      >
        <ListItemIcon>
          <SaveIcon color="primary" fontSize="small" />
        </ListItemIcon>
        <ListItemText>Save</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem
        sx={{ width: "15rem" }}
        data-testid={`delete-prop-${propName}`}
        onClick={() => {
          handleDelete();
        }}
      >
        <ListItemIcon>
          <Delete color="primary" fontSize="small" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </>
  );
};

export const PropertyViewMenuContent: React.FC<{
  onClose: PopoverProps["onClose"];
  propName: string | undefined;
}> = ({ propName, onClose }) => {
  return (
    <MenuList>
      <EditPropertyMenuItems propName={propName} onClose={onClose} />
    </MenuList>
  );
};

export const PropertyEditMenu: React.FC<
  MenuProps & {
    propName: string | undefined;
  }
> = ({ propName, ...menuProps }) => {
  const { onClose } = menuProps;

  return (
    <Menu {...menuProps}>
      <PropertyViewMenuContent propName={propName} onClose={onClose} />
    </Menu>
  );
};

export const PropertiesMenu: React.FC<UseMenuProps> = ({ ...props }) => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { folderProps } = useFolderPropertyTypes(selectedNotebookPageId);
  const properties = Object.values(folderProps ?? {});
  const {
    anchorEl: anchorElAddProperty,
    handleOpen: handleOpenAddProperty,
    onClose: onCloseAddProperty,
    open: openAddProperty,
    setAnchorEl: setAnchorElAddProperty,
    anchorToClick: anchorToClickAddProperty,
  } = useMenu();
  const {
    anchorEl: anchorElViewProperty,
    handleOpen: handleOpenViewProperty,
    onClose: onCloseViewProperty,
    open: openViewProperty,
    setAnchorEl: setAnchorElViewProperty,
    anchorToClick: anchorToClickViewProperty,
  } = useMenu();
  const elRef = useRef<HTMLElement | null>(null);
  const [selectedProp, setSelectedProp] = useState<string | undefined>(
    undefined,
  );

  // useEffect(() => {
  //   if (!elRef.current) return;
  //   setAnchorElViewProperty(elRef.current);
  // }, [elRef.current]);

  // useEffect(() => {
  //   if (properties.length !== 0) {
  //     setSelectedProp(properties[0].name);
  //   }
  // }, [properties.length]);

  return (
    <Menu
      {...props}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <PropertyEditMenu
        anchorEl={anchorElViewProperty}
        onClose={onCloseViewProperty}
        open={openViewProperty}
        propName={selectedProp}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      />
      <AddPropertyMenu
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        anchorEl={anchorElAddProperty}
        onClose={onCloseAddProperty}
        open={openAddProperty}
      />
      <MenuList>
        <MenuItem sx={{ width: "15rem" }} disabled={true}>
          <ListItemText>Properties</ListItemText>
        </MenuItem>
        {properties?.map((prop, i) => {
          return (
            <MenuItem
              key={i}
              sx={{ width: "15rem" }}
              ref={(r) => {
                // if (i === 0) {
                //   elRef.current = r;
                // }
              }}
              onClick={(e) => {
                handleOpenViewProperty(e);
                setSelectedProp(prop.name);
              }}
            >
              <ListItemText>{prop.name}</ListItemText>
              <ListItemSecondaryAction>
                <IconButton color="primary">
                  <ChevronRight />
                </IconButton>
              </ListItemSecondaryAction>
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem
          sx={{ width: "15rem" }}
          onClick={handleOpenAddProperty}
          // ref={(r) => {
          //   elRef.current = r;
          // }}
        >
          <ListItemIcon>
            <Add color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText>Add Property</ListItemText>
          <ListItemSecondaryAction>
            <IconButton color="primary">
              <ChevronRight />
            </IconButton>
          </ListItemSecondaryAction>
        </MenuItem>
      </MenuList>
    </Menu>
  );
};
